/**
 * Die Uhr hinter der Schlafscheibe: wie lange hat niemand das iPad angerührt?
 *
 * Sie steht bewusst **ohne React** hier. Das Bauteil (`Schlafscheibe.tsx`) hängt nur die
 * Ereignisse des Fensters daran und zeigt an, was sie meldet — alles, was schiefgehen kann
 * (zu früh, gar nicht, zweimal, nach dem Abräumen), liegt damit an einer Stelle, die sich
 * ohne Bildschirm prüfen lässt (`inaktivitaet.test.ts`).
 */

/**
 * Fünf Minuten (Vorgabe des Nutzers, 2026-09-12). Bewusst **keine** Einstellung: es ist eine
 * Schonung für Bildschirm und Umstehende, kein Betriebsparameter.
 */
export const SCHLAF_NACH_MS = 5 * 60 * 1000;

export interface Inaktivitaetsuhr {
  /**
   * Es ist etwas geschehen — von vorn zählen.
   *
   * Zugleich der Weg zurück aus dem Schlaf: nach einem Ablauf steht die Uhr still, bis sie
   * hierüber neu gestellt wird.
   */
  tippen(): void;
  /**
   * Anhalten, solange etwas läuft, das keine Berührung erzeugt — eine Video- oder
   * Tonaufnahme, ein Bogenabzug. Mehrfaches Anhalten wird gezählt: zwei Gründe halten die
   * Uhr an, und erst wenn beide weg sind, läuft sie wieder.
   */
  pausiere(): void;
  /** Der Grund ist weg. Ist es der letzte, laufen die vollen fünf Minuten neu an. */
  setzeFort(): void;
  /** Beim Abbau des Bauteils: nichts soll mehr melden. */
  raeumeAuf(): void;
}

export function starteInaktivitaetsuhr(beiAblauf: () => void): Inaktivitaetsuhr {
  let handle: ReturnType<typeof setTimeout> | null = null;
  let pausen = 0;
  let abgeraeumt = false;

  const stelleAb = (): void => {
    if (handle !== null) {
      clearTimeout(handle);
      handle = null;
    }
  };

  const stelleNeu = (): void => {
    stelleAb();
    if (abgeraeumt || pausen > 0) return;
    handle = setTimeout(() => {
      handle = null;
      // ⚠️ **Die Uhr läuft danach nicht weiter.** Ohne das stapelten sich die Meldungen,
      // während geschlafen wird, und ein Aufwecken fände eine Uhr vor, die längst wieder
      // abgelaufen ist. Erst `tippen()` stellt sie neu.
      beiAblauf();
    }, SCHLAF_NACH_MS);
  };

  stelleNeu();

  return {
    tippen: () => stelleNeu(),
    pausiere: () => {
      pausen += 1;
      stelleAb();
    },
    setzeFort: () => {
      if (pausen === 0) return;
      pausen -= 1;
      // ⚠️ Volle fünf Minuten, nicht der Rest von vorhin: wer gerade eine Aufnahme beendet
      // hat, schaut auf den Bogen — es wäre die falsche Sekunde, ihn zu verdunkeln.
      if (pausen === 0) stelleNeu();
    },
    raeumeAuf: () => {
      abgeraeumt = true;
      stelleAb();
    },
  };
}

/**
 * Läuft gerade etwas, das keine Berührung erzeugt?
 *
 * ⚠️ **Die einzige Stelle, an der diese Frage beantwortet wird.** Sie steht hier und nicht im
 * Bauteil, damit sie sich ohne Bildschirm prüfen lässt — und damit ein späterer dritter Grund
 * (etwa ein laufender Upload) genau eine Zeile kostet.
 *
 * - `schlaeft`: die Scheibe liegt bereits. Sie schluckt Berührungen, aber `pointerdown`
 *   erreicht das Fenster trotzdem — ohne diese Wache stellte sich die Uhr im Schlaf neu.
 * - `medienAufnahme`: Ton oder Video nimmt auf. Der Prüfer hält dabei die Kamera und rührt
 *   den Bildschirm minutenlang nicht an; eine Scheibe über dem Kamerabild wäre das Letzte,
 *   was er gebrauchen kann.
 * - `shotStamp`: der Bogenabzug läuft (s. `features/bogen/CaptureOverlay.tsx`). Er dauert
 *   keine fünf Minuten, aber eine Verdunkelung mitten hinein käme ins Bild.
 * - `mitteilungOffen`: eine Mitteilung der Prüfungsleitung wartet auf „Gelesen“. Sie muss
 *   bestätigt werden (Entscheidung des Nutzers, 2026-10-08); unter der Scheibe sähe sie
 *   niemand, und der Abgleich quittierte sie nie.
 */
export function sollPausieren(zustand: {
  schlaeft: boolean;
  medienAufnahme: boolean;
  shotStamp: string | null;
  mitteilungOffen: boolean;
}): boolean {
  return zustand.schlaeft || zustand.medienAufnahme || zustand.shotStamp !== null || zustand.mitteilungOffen;
}
